import { Link } from "react-router-dom";
import { ExternalLink, ShoppingCart, Wrench, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  return (
    <div
      data-testid={`product-card-${product.product_id}`}
      className="ici-card-lift group relative rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col ici-fade-up"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <Link to={`/product/${product.slug}`} className="block relative aspect-[4/3] bg-slate-50 overflow-hidden">
        {product.main_image ? (
          <img
            src={product.main_image}
            alt={product.title}
            loading="lazy"
            className="ici-img-zoom absolute inset-0 w-full h-full object-contain p-4"
          />
        ) : (
          <div className="absolute inset-0 ici-skeleton" />
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {product.condition && (
            <Badge variant="secondary" className="bg-[#0055FF] text-white border-transparent uppercase tracking-widest text-[10px] font-mono">{product.condition}</Badge>
          )}
          {product.has_test_report && (
            <Badge variant="secondary" className="bg-slate-900 text-white border-transparent uppercase tracking-widest text-[10px] font-mono">Test Report</Badge>
          )}
          {product.is_upgraded && (
            <Badge variant="secondary" className="bg-emerald-600 text-white border-transparent uppercase tracking-widest text-[10px] font-mono">Upgraded</Badge>
          )}
        </div>
        {product.is_set && (
          <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur border border-slate-200 rounded-md px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-slate-700">
            Set × {product.quantity_included}
          </div>
        )}
      </Link>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {product.manufacturer && <span>{product.manufacturer}</span>}
          {product.engine_size && <span className="text-slate-300">·</span>}
          {product.engine_size && <span>{product.engine_size}</span>}
        </div>
        <Link to={`/product/${product.slug}`} className="text-sm font-heading font-semibold text-slate-900 line-clamp-2 hover:text-[#0055FF] transition-colors min-h-[2.6rem]">
          {product.title}
        </Link>
        <div className="text-xs text-slate-500 font-mono line-clamp-1">{product.fitment_summary}</div>
        {product.sku && <div className="text-[11px] text-slate-400 font-mono">SKU {product.sku}</div>}
        <div className="mt-auto flex items-end justify-between pt-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Price</div>
            <div className="text-xl font-heading font-bold text-slate-900">{formatPrice(product.price)}</div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Button
              data-testid={`add-to-cart-${product.product_id}`}
              size="sm"
              onClick={() => add(product)}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white gap-1.5"
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Add
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
          {product.ebay_url && (
            <a
              data-testid={`buy-ebay-${product.product_id}`}
              href={product.ebay_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0055FF] hover:underline flex items-center gap-1"
            >
              Buy on eBay <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <span className="text-slate-300">·</span>
          <Link to={`/fitment-assistance?product=${encodeURIComponent(product.title)}`} className="text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <Wrench className="w-3 h-3" /> Check fitment
          </Link>
        </div>
      </div>
    </div>
  );
}
